import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";

import { getCookie } from "@insite/client-framework/Common/Cookies";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomArrowRightIcon from "@tSurya/Icons/CustomArrowRightIcon";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    depth = "depth",
    showMyAccountPageLinks = "showMyAccountPageLinks",
    nodeId = "nodeId",
    leftMargin = "leftMargin",
    marginBottom = "marginBottom",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.depth]: number;
        [fields.nodeId]: LinkFieldValue;
        [fields.leftMargin]: number;
        [fields.marginBottom]: number;
        [fields.customCSS]: string;
        [fields.showMyAccountPageLinks]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const pageNodeId = ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId;
    const accountPageNodeId = getPageLinkByPageType(state, "MyAccountPage")?.id;
    const nodeId: string = ownProps.fields.showMyAccountPageLinks ? accountPageNodeId || pageNodeId : pageNodeId;

    return {
        rootPageLink: getPageLinkByNodeId(state, nodeId),
        pageTitle: getCurrentPage(state).fields.title,
        // rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        userRole: state.context.session.userRoles,
        isSuryaInternalUser: state.context.session.properties?.isSuryaInternalUser,
        sessionData: state.context.session,
    };
};

interface ListStyleProp {
    leftMargin: number;
    marginBottom: number;
}

export interface IconStyles {
    arrowRightIconStyles?: IconProps;
    iconLinkStyles?: InjectableCss;
}

export const iconStyles: IconStyles = {
    arrowRightIconStyles: {
        height: 16,
        width: 16,
    },
    iconLinkStyles: {
        css: css`
            position: absolute;
            top: 16px;
            right: 0px;
        `,
    },
};
export interface NavigationLinkAccordianStyles {
    accordionSection?: AccordionSectionPresentationProps;
    mobileHiddenContainerStyles?: InjectableCss;
}
export const navigationLinkAccordianStyles: NavigationLinkAccordianStyles = {
    mobileHiddenContainerStyles: {
        css: css`
            @media (min-width: 993px) {
                display: none !important;
            }
        `,
    },
    accordionSection: {
        headerProps: {
            css: css`
                > button {
                    border: none !important;
                    background: #f1efea !important;
                    justify-content: space-between !important;
                    > span:nth-child(1) {
                        font-size: 28px;
                        font-weight: 600;
                        line-height: 36px;
                        color: #323132;
                        text-align: left;
                        // text-transform: uppercase;
                    }
                    > span:nth-child(2) {
                        height: 20px;
                        width: 20px;
                        max-height: 20px;
                        max-width: 20px;
                    }
                }
            `,
        },
        expandIconProps: {
            size: 24,
        },
        collapseIconProps: {
            size: 24,
        },
        panelProps: {
            css: css`
                border: none !important;
            `,
        },
    },
};

const accordianStyles = navigationLinkAccordianStyles;
const styles = iconStyles;

const ListStyle = styled.ul<ListStyleProp>`
    margin-top: 15px;
    margin-left: ${props => props.leftMargin}px;
    margin-left: -18px !important;
    li {
        margin-bottom: ${props => props.marginBottom}px;
    }
    .navigation-list-item {
        border-bottom: 1px solid #f1efea;
        margin-bottom: 0px;
        display: flex;
        align-items: center;
        position: relative;
        top: 0px;
        padding: 15px 20px 15px 20px;
    }
    .navigation-list-link {
        > span {
            font-family: Mulish;
            color: #323132;
            font-size: 14px;
            font-weight: 400;
            line-height: 22px;
        }
    }
    .bold-link {
        > span {
            font-weight: 800;
        }
    }
    ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(theme, [
            null,
            null,
            css`
                li {
                    margin-bottom: 15px;
                }
            `,
            css`
                li {
                    margin-bottom: 15px;
                }
            `,
            null,
        ])}
`;

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const NavigationList = (props: Props) => {
    let isDashboard;
    if (typeof window !== "undefined") {
        isDashboard = window?.location?.pathname?.endsWith("MyAccount");
    }
    if (!props.rootPageLink || !props.rootPageLink.children) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${props.fields.customCSS}
        `,
    };

    const renderLink = (pageLink: PageLinkModel, currentDepth: number) => {
        let selectedPageLink;
        if (typeof window !== "undefined") {
            selectedPageLink = window?.location?.pathname?.includes(pageLink?.url);
        }

        if (currentDepth > props.fields.depth) {
            return;
        }

        const excludeNavigationListLinksMyAccount: any = siteMessage("Exclude_NavigationList_Links_MyAccount");
        let excludeLinksMyAccountArr: string[] = [];

        if (typeof excludeNavigationListLinksMyAccount === "string" && excludeNavigationListLinksMyAccount.trim() !== "") {
            excludeLinksMyAccountArr = excludeNavigationListLinksMyAccount.split("|");
        }

        if (excludeLinksMyAccountArr.includes(pageLink.title)) {
            return;
        }
        if (
            pageLink.excludeFromNavigation ||
            (pageLink.type === "ChangeCustomerPage" && (!props.displayChangeCustomerLink || props.sessionData?.properties?.hasMultipleCustomers === "False")) ||
            (pageLink.title === "Financial Transactions" && props.userRole !== "Administrator" && props.userRole !== "Buyer3") ||
            (pageLink.title === "Manage Users" && props.isSuryaInternalUser !== "true") ||
            ((pageLink.title === "Order History" ||
                pageLink.title === "Returns" ||
                pageLink.title === "Credit Memos" ||
                pageLink.title === "Order Status" ||
                pageLink.title === "Display Order Form" ||
                pageLink.title === "Cut and Sew Rug Order" ||
                pageLink.title === "Transactions" ||
                pageLink.title === "Upload Order" ||
                pageLink.title === "Saved Orders" ||
                pageLink.title === "Wallet" ||
                pageLink.title === "Addresses") &&
                props.userRole === "Collaborator")
        ) {
            return;
        }

        if (pageLink.type === "VmiUsersPage") {
            const currentMode = getCookie(NavigationModeCookieName);
            if (!currentMode || currentMode === "Storefront") {
                return;
            }
        }

        const nextDepth = currentDepth + 1;

        return (
            <li key={pageLink.id} className="navigation-list-item ">
                <Link
                    className={`navigation-list-link ${selectedPageLink ? "bold-link" : ""}`}
                    data-test-selector={`navigationList-${pageLink.title}`}
                    href={pageLink.url}
                >
                    {pageLink.title}
                </Link>

                {selectedPageLink && (
                    <Link href={pageLink.url} {...styles.iconLinkStyles}>
                        <Icon src={CustomArrowRightIcon} {...styles.arrowRightIconStyles} />
                    </Link>
                )}
                {pageLink.children && pageLink.children.length > 0 && (
                    <ListStyle marginBottom={props.fields.marginBottom} leftMargin={props.fields.leftMargin} key={pageLink.id}>
                        {pageLink.children.map(link => renderLink(link, nextDepth))}
                    </ListStyle>
                )}
            </li>
        );
    };

    return (
        <>
            <Hidden below="lg">
                <StyledWrapper {...customCssWrapper}>
                    <ListStyle
                        className="navigation-list"
                        marginBottom={props.fields.marginBottom}
                        leftMargin={props.fields.leftMargin}
                        data-test-selector="navigationList"
                    >
                        <li className="navigation-list-item ">
                            <Link className={`navigation-list-link ${isDashboard ? "bold-link" : ""}`} href="/MyAccount">
                                {translate("Dashboard")}
                            </Link>

                            {isDashboard && (
                                <Link href="/MyAccount" {...styles.iconLinkStyles}>
                                    <Icon src={CustomArrowRightIcon} {...styles.arrowRightIconStyles} />
                                </Link>
                            )}
                        </li>
                        {props.rootPageLink.children.map(link => renderLink(link, 1))}
                    </ListStyle>
                </StyledWrapper>
            </Hidden>
            <Hidden above="lg" {...navigationLinkAccordianStyles.mobileHiddenContainerStyles}>
                <ManagedAccordionSection title={translate("Menu")} {...accordianStyles.accordionSection} initialExpanded={false}>
                    <StyledWrapper {...customCssWrapper}>
                        <ListStyle
                            className="navigation-list"
                            marginBottom={props.fields.marginBottom}
                            leftMargin={props.fields.leftMargin}
                            data-test-selector="navigationList"
                        >
                            <li className="navigation-list-item ">
                                <Link className={`navigation-list-link ${isDashboard ? "bold-link" : ""}`} href="/MyAccount">
                                    {translate("Dashboard")}
                                </Link>

                                {isDashboard && (
                                    <Link href="/MyAccount" {...styles.iconLinkStyles}>
                                        <Icon src={CustomArrowRightIcon} {...styles.arrowRightIconStyles} />
                                    </Link>
                                )}
                            </li>
                            {props.rootPageLink.children.map(link => renderLink(link, 1))}
                        </ListStyle>
                    </StyledWrapper>
                </ManagedAccordionSection>
            </Hidden>
        </>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavigationList),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.depth,
                displayName: "Depth",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
                tab: basicTab,
            },
            {
                name: fields.showMyAccountPageLinks,
                displayName: "Show Account Page Links",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: (item: HasFields, advancedFeaturesEnabled?: boolean, theme?: Partial<BaseTheme>, pageType?: string) => {
                    return pageType !== "MyAccountPage";
                },
            },
            {
                name: fields.leftMargin,
                displayName: "Left Margin",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
